import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BottomCTA, IconPersonFill, TopNavigation } from "@hamlak/ui";
import { apiFetch, isApiError } from "@/api/client";
import { ep } from "@/api/endpoints";
import { qk } from "@/api/queryKeys";
import type { Profile, ProfilePatchRequest, ProfileResponse, ProfilesResponse } from "@/api/types";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { ProfileForm, type ProfileFormValue } from "../auth/ProfileForm";
import "../auth/auth.css";

/**
 * 대화 프로필 편집 /profile/:id/edit(spec 7절, 시안 807:14680). 이름·소개·성별만 — 사진·삭제·기본 전환은 1차 범위 밖. 상단 "대화 속 나"는 장식(AC0-291).
 * 초기값은 GET /me/profiles, 저장은 바뀐 필드만 PATCH /me/profiles/:id. 저장하면 마이페이지 캐시(authStore.profile)도 갱신.
 */
export function ProfileEditPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const query = useQuery({ queryKey: qk.meProfiles, queryFn: () => apiFetch<ProfilesResponse>(ep.meProfiles), staleTime: 0 });
  const profile = query.data?.profiles.find((p) => p.id === id);

  const back = () => {
    // 직접 진입(히스토리 없음)이면 /me 로
    if (typeof window !== "undefined" && (window.history.state as { idx?: number } | null)?.idx) navigate(-1);
    else navigate("/me", { replace: true });
  };

  return (
    <main className="page auth profile" data-testid="profile-edit-page">
      <TopNavigation title={t("page.profileEdit")} right={null} onBack={back} />
      {profile ? (
        <EditForm profile={profile} onDone={back} />
      ) : query.isError || query.data ? (
        <p className="auth__form" role="alert">
          {t("error.generic")}
        </p>
      ) : null}
    </main>
  );
}

function EditForm({ profile, onDone }: { profile: Profile; onDone: () => void }) {
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const queryClient = useQueryClient();
  const initial: ProfileFormValue = { name: profile.name, bio: profile.bio ?? "", gender: profile.gender };
  const [form, setForm] = useState<ProfileFormValue>(initial);
  const [nameError, setNameError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);

  const name = form.name.trim();
  const bio = form.bio.trim();
  // 바뀐 필드만 보낸다. 소개를 비우면 null(제거).
  const body: ProfilePatchRequest = {
    ...(name !== profile.name && name ? { name } : {}),
    ...(bio !== (profile.bio ?? "") ? { bio: bio || null } : {}),
    ...(form.gender !== profile.gender ? { gender: form.gender } : {}),
  };
  const changed = name !== profile.name || Object.keys(body).length > 0;

  const submit = async () => {
    if (!name) {
      setNameError(t("profile.name.required"));
      return;
    }
    setPending(true);
    try {
      const res = await apiFetch<ProfileResponse>(ep.meProfile(profile.id), { method: "PATCH", body });
      const { id, name: savedName, gender } = res.profile;
      if (useAuthStore.getState().profile?.id === id) useAuthStore.setState({ profile: { id, name: savedName, gender } });
      // 목록 캐시도 저장본으로 — 다시 열 때 낡은 값으로 폼이 초기화돼 저장이 되돌려 쓰는 것을 막는다
      queryClient.setQueryData<ProfilesResponse>(qk.meProfiles, (old) => old && { ...old, profiles: old.profiles.map((p) => (p.id === res.profile.id ? res.profile : p)) });
      onDone();
    } catch (e) {
      showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("error.generic"));
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <div className="auth__form">
        <div className="profile__avatar">
          <span className="profile__avatar-circle" aria-hidden>
            <IconPersonFill size={36} />
          </span>
          <div className="profile__avatar-text">
            <span className="profile__avatar-title">{t("profile.me.title")}</span>
            <span className="profile__avatar-description">{t("profile.me.description")}</span>
          </div>
        </div>
        <ProfileForm
          value={form}
          onChange={(v) => {
            setForm(v);
            setNameError(undefined);
          }}
          errors={{ name: nameError }}
          showDefaultNotice={false}
        />
      </div>
      <BottomCTA primary={{ children: t("profile.edit.cta"), disabled: !changed || pending, loading: pending, onClick: () => void submit() }} />
    </>
  );
}
